import BaseService from './BaseService';
import {
  Keyword,
  ScrapedAd,
  AnalysisPrompt,
  LlmAnalysis,
  AdListResponse,
  AnalysisListResponse,
} from 'pages/VideoCreative/types';

class AdLibraryService extends BaseService {
  private basePath = 'v1/ad-library';

  // Keywords
  async listKeywords(activeOnly = false): Promise<{ keywords: Keyword[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/keywords`, { params: { activeOnly } }),
      'Failed to fetch keywords',
    );
  }

  async createKeyword(data: Partial<Keyword>): Promise<Keyword | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/keywords`, data),
      'Failed to create keyword',
    );
  }

  /** What this keyword has pulled on each side, for the delete confirmation. */
  async keywordUsage(keywordId: string): Promise<{ videoAdCount: number; staticAdCount: number } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/keywords/${keywordId}/usage`),
      'Failed to load keyword usage',
    );
  }

  async updateKeyword(keywordId: string, data: Partial<Keyword>): Promise<Keyword | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/keywords/${keywordId}`, data),
      'Failed to update keyword',
    );
  }

  async deleteKeyword(keywordId: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/keywords/${keywordId}`),
      'Failed to delete keyword',
    );
  }

  // Scraping
  async triggerScrape(params: { keywordId?: string; keyword?: string; scrapeDepth?: number; startDateMin?: string; startDateMax?: string }): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/scrape`, params),
      'Failed to trigger scrape',
    );
  }

  // Pipeline
  async runPipeline(adId: string, promptIds?: string[], analysisModel?: string, extractionModel?: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/${adId}/run-pipeline`, { promptIds, analysisModel, extractionModel }),
      'Failed to run pipeline',
    );
  }

  // Manual ad upload — creates a CI_ADS row from user fields + (optionally) a
  // video file or URL, then auto-runs the transcribe → analyze → extract
  // pipeline. Multipart so it can carry the file alongside text fields.
  async uploadAd(formData: FormData): Promise<{ adId: string; pipelineStatus?: string; currentStep?: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/upload`, formData),
      'Failed to upload ad',
    );
  }

  // Ads
  async listAds(params: {
    keywordId?: string;
    limit?: number;
    offset?: number;
    transcriptionStatus?: string;
    startDateFrom?: string;
    startDateTo?: string;
    fetchedFrom?: string;
    fetchedTo?: string;
    updatedFrom?: string;
    updatedTo?: string;
    awarenessLevel?: string;
    hookDevice?: string;
    persuasionAngle?: string;
    proofType?: string;
    narrativeStructure?: string;
    primaryEmotion?: string;
    presenter?: string;
    productionFormat?: string;
    tone?: string;
    offerType?: string;
    ctaType?: string;
    tag?: string[];
  } = {}): Promise<AdListResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads`, { params }),
      'Failed to fetch ads',
    );
  }

  // Tags (free-form, multiple per ad)
  async listAdTags(): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/tags`),
      'Failed to fetch ad tags',
    );
  }

  async addAdTag(adId: string, tag: string): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/${adId}/tags`, { tag }),
      'Failed to add tag',
    );
  }

  async removeAdTag(adId: string, tag: string): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/ads/${adId}/tags/${encodeURIComponent(tag)}`),
      'Failed to remove tag',
    );
  }

  async getAd(adId: string): Promise<ScrapedAd | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads/${adId}`),
      'Failed to fetch ad',
    );
  }

  async transcribeAd(adId: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/${adId}/transcribe`),
      'Failed to transcribe ad',
    );
  }

  async batchTranscribe(adIds: string[]): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/batch-transcribe`, { adIds }),
      'Failed to batch transcribe',
    );
  }

  // Prompts
  async listPrompts(): Promise<{ prompts: AnalysisPrompt[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/prompts`),
      'Failed to fetch prompts',
    );
  }

  async createPrompt(data: Partial<AnalysisPrompt>): Promise<AnalysisPrompt | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/prompts`, data),
      'Failed to create prompt',
    );
  }

  async updatePrompt(promptId: string, data: Partial<AnalysisPrompt>): Promise<AnalysisPrompt | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/prompts/${promptId}`, data),
      'Failed to update prompt',
    );
  }

  async deletePrompt(promptId: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/prompts/${promptId}`),
      'Failed to delete prompt',
    );
  }

  // Analysis
  async analyzeAd(adId: string, promptId: string, model?: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/${adId}/analyze`, { promptId, model }),
      'Failed to analyze ad',
    );
  }

  async batchAnalyze(adIds: string[], promptId: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/batch-analyze`, { adIds, promptId }),
      'Failed to batch analyze',
    );
  }

  async listAnalyses(params: { adId?: string; limit?: number; offset?: number } = {}): Promise<AnalysisListResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/analyses`, { params }),
      'Failed to fetch analyses',
    );
  }

  async getAnalysis(analysisId: string): Promise<LlmAnalysis | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/analyses/${analysisId}`),
      'Failed to fetch analysis',
    );
  }

  async deleteAnalysis(analysisId: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/analyses/${analysisId}`),
      'Failed to delete analysis',
    );
  }

  // Automation
  async updateAutomation(keywordId: string, data: { autoTranscribe?: boolean; autoAnalyze?: boolean; analysisPromptIds?: string[] | null }): Promise<Keyword | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/automation/${keywordId}`, data),
      'Failed to update automation',
    );
  }

  /** The Ad Library URL these keyword settings would search — built server-side. */
  async keywordSearchUrl(body: {
    keyword: string;
    dateRangePreset?: string;
    startDateMin?: string | null;
    startDateMax?: string | null;
    pageIds?: string[];
    pageGroupIds?: string[];
  }): Promise<{ url: string; advertiserCount: number } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/keyword-search-url`, body),
      'Could not build the Ad Library link',
    );
  }

}

export default AdLibraryService;
